// 儲存（SD 卡）— cluster 0x8090（spec 21 §3.10）。
// Format = 破壞性 CMD → 雙重確認（dialog 警告 + 輸入裝置名稱），送出後輪詢
// op:get Status 直到離開 formatting(4)。Unmount = 單次確認。
// Status enum: 1 normal / 2 abnormal / 3 no-space / 4 formatting / 5 no-sd。

import { useEffect, useRef, useState, useContext } from 'react';
import { PhoneCamContext, usePhoneT } from '../../../lib/phoneCam';
import type { TFunction } from 'i18next';
import { SD_CAPACITY, SD_FORMAT, SD_PRESENT, SD_STATUS, SD_UNMOUNT } from './ids';
import { frameValue, opCmd, opGet, useAttr } from './ops';
import { ConfirmDialog, ReadonlyRow, SectionCard } from './widgets';

// ⚠️ i18n: status 顯示名須在使用點以 t 求值(語言切換要即時),不可凍結成模組頂層 const。
function statusLabel(t: TFunction, n: number): string | undefined {
  const map: Record<number, string> = {
    1: t('deviceSettings.sdStatusNormal'),
    2: t('deviceSettings.sdStatusAbnormal'),
    3: t('deviceSettings.sdStatusNoSpace'),
    4: t('deviceSettings.sdStatusFormatting'),
    5: t('deviceSettings.sdStatusNoCard'),
  };
  return map[n];
}

export function SdcardSection({
  deviceId,
  canControl,
  isOwner,
  wsOpen,
  deviceName,
  recordLocationSdcard,
}: {
  deviceId: string;
  /** §3.10 卸載＝control（可逆，插回去即復原）。 */
  canControl: boolean;
  /** 格式化＝owner-only（破壞性，抹掉 owner 的全部 SD 錄影）。授權 SSOT＝
   *  common/04 §4.18 `write_authz`；signaling 對 SD_FORMAT 驗 role=="owner"。 */
  isOwner: boolean;
  wsOpen: boolean;
  deviceName: string;
  recordLocationSdcard: boolean;
}) {
  const { t } = usePhoneT();
  const phone = useContext(PhoneCamContext);   // 手機攝影機：「SD 卡」→「手機空間」（ls-loop MW）
  const present = useAttr(deviceId, SD_PRESENT, wsOpen);
  const status = useAttr(deviceId, SD_STATUS, wsOpen);
  const capacity = useAttr(deviceId, SD_CAPACITY, wsOpen);
  const [dialog, setDialog] = useState<'format' | 'unmount' | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  if (!wsOpen) {
    return (
      <SectionCard title={t('deviceSettings.storageSdcard')}>
        <p className="text-secondary" style={{ margin: 0 }}>
          {t('deviceSettings.connecting')}
        </p>
      </SectionCard>
    );
  }

  const statusNum = typeof status.value === 'number' ? status.value : null;
  const formatting = statusNum === 4 || polling;
  const noCard = present.status === 'ready' && (present.value === false || present.value === 0);

  // 送出後輪詢 op:get Status 直到離開 4 formatting（§3.10）
  async function pollUntilDone() {
    setPolling(true);
    for (let i = 0; i < 40 && alive.current; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      if (!alive.current) break;
      try {
        const f = await opGet(deviceId, SD_STATUS);
        const v = frameValue(f);
        if (typeof v === 'number' && v !== 4) {
          status.refresh();
          capacity.refresh();
          setMessage(t('deviceSettings.formatDone'));
          break;
        }
      } catch {
        /* keep polling */
      }
    }
    if (alive.current) setPolling(false);
  }

  async function runCmd(kind: 'format' | 'unmount') {
    setBusy(true);
    setMessage(null);
    try {
      await opCmd(deviceId, kind === 'format' ? SD_FORMAT : SD_UNMOUNT);
      setDialog(null);
      if (kind === 'format') {
        setMessage(t('deviceSettings.formatStarted'));
        void pollUntilDone();
      } else {
        setMessage(t('deviceSettings.unmountDone'));
        status.refresh();
      }
    } catch (err) {
      setMessage(err instanceof Error ? t('deviceSettings.cmdFailedReason', { msg: err.message }) : t('deviceSettings.cmdFailed'));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  return (
    <SectionCard title={t('deviceSettings.storageSdcard')}>
      {noCard ? (
        <p className="text-secondary" style={{ margin: 0 }}>
          {t('deviceSettings.noSdcardDetected')}
        </p>
      ) : (
        <>
          {!phone && <ReadonlyRow label={t('deviceSettings.sdPresent')} attr={present} render={(v) => (v === true || v === 1 ? t('deviceSettings.sdInserted') : t('deviceSettings.sdNotInserted'))} />}
          <ReadonlyRow label={t('deviceSettings.status')} attr={status} render={(v) => (typeof v === 'number' ? (statusLabel(t, v) ?? String(v)) : String(v))} />
          <ReadonlyRow label={t('deviceSettings.capacity')} attr={capacity} />
          {/* 手機攝影機沒有卸載 / 格式化（錄影容器是手機空間裡的檔案；ls-loop MW） */}
          {canControl && !phone && (
            <div style={{ display: 'flex', gap: 'var(--spacing-4)', paddingTop: 'var(--spacing-4)' }}>
              <button className="btn-ghost" disabled={busy || formatting} onClick={() => setDialog('unmount')}>
                {t('deviceSettings.unmountSdcard')}
              </button>
              {isOwner && (
                <button className="btn-danger" data-testid="sd-format" disabled={busy || formatting} onClick={() => setDialog('format')}>
                  {formatting ? t('deviceSettings.formatting') : t('deviceSettings.formatSdcard')}
                </button>
              )}
            </div>
          )}
          {message && (
            <p className="text-secondary" role="status" style={{ margin: 0 }}>
              {message}
            </p>
          )}
        </>
      )}

      {dialog === 'unmount' && (
        <ConfirmDialog
          spec={{
            title: t('deviceSettings.unmountConfirmTitle'),
            body: t('deviceSettings.unmountConfirmBody'),
            confirmLabel: t('deviceSettings.unmount'),
          }}
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => void runCmd('unmount')}
        />
      )}
      {dialog === 'format' && (
        <ConfirmDialog
          spec={{
            title: t('deviceSettings.formatConfirmTitle'),
            body: (
              <>
                <p style={{ marginTop: 0 }}>{t('deviceSettings.formatConfirmBody')}</p>
                {recordLocationSdcard && <p style={{ marginBottom: 0 }}>{t('deviceSettings.formatConfirmSdcardActive')}</p>}
              </>
            ),
            confirmLabel: t('deviceSettings.formatConfirmLabel'),
            danger: true,
            requireText: deviceName,
            requireTextHint: t('deviceSettings.typeDeviceNameHint', { name: deviceName }),
          }}
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => void runCmd('format')}
        />
      )}
    </SectionCard>
  );
}
